'use client';
import type { InterfaceEntity } from '@unifyapps/defs/types/interface';
import { useMemo } from 'react';
import { debugSSR } from '../../../../../../../consts/debug';
import { useEnvVariables } from '../../../../../../../hooks/useEnvVariables';
import { useFetchInterfacePermissions } from '../../../../../../access-control/hooks/useFetchInterfacePermissions';
import { applyInterfaceOverride } from '../../../../../../interfaceOverride/applyInterfaceOverride';
import type { InterfaceOverrideSource } from '../../../../../../interfaceOverride/useFetchDeployedInterfaceOverride';
import { useFetchDeployedInterfaceOverride } from '../../../../../../interfaceOverride/useFetchDeployedInterfaceOverride';
import InterfaceHelper from '../../../../../../stores/InterfaceStore/helper/InterfaceHelper';
import type { UseFetchInterfaceAppResourcesHookType } from '../../../../types';

type AppParams = Parameters<UseFetchInterfaceAppResourcesHookType>[0];

interface FromRecordArgs {
  interfaceId: AppParams['interfaceId'];
  client: AppParams['client'];
  mode: AppParams['mode'];
  interfaceRecord: InterfaceEntity | undefined;
  isLoadingInterfaceRecord: boolean;
  errorInterfaceRecord: unknown;
  // which route the caller resolved the base record on, so the override fetch matches it
  overrideSource: InterfaceOverrideSource;
}

/**
 * Shared tail for the Authorization / PlatformAuthorization app hooks. Given an already-resolved app
 * interface record, loads permissions + env variables. Record resolution (by domain/host vs by id)
 * is the only thing the two strategies differ on, so it stays in each hook.
 */
export function useAuthAppResourcesFromRecord({
  interfaceId,
  client,
  mode,
  interfaceRecord: baseInterfaceRecord,
  isLoadingInterfaceRecord,
  errorInterfaceRecord,
  overrideSource,
}: FromRecordArgs) {
  // same override chokepoint as the Deployed strategy, so login / SSO / governance pages render the
  // white-labelled branding too. gated on the flag; hydrates from the server-prefetched key.
  const { override, isLoading: isLoadingOverride } = useFetchDeployedInterfaceOverride({
    interfaceId: baseInterfaceRecord?.id,
    isOverridden: InterfaceHelper.isInterfaceOverridden(baseInterfaceRecord),
    source: overrideSource,
  });
  const interfaceRecord = useMemo(
    () =>
      override && baseInterfaceRecord
        ? applyInterfaceOverride(baseInterfaceRecord, override)
        : baseInterfaceRecord,
    [baseInterfaceRecord, override],
  );

  const {
    permissions,
    isLoading: permissionsLoading,
    error: permissionsError,
  } = useFetchInterfacePermissions({
    interfaceId,
    client,
    mode,
  });

  const {
    envVariables,
    isLoading: envVariablesLoading,
    error: envVariablesError,
  } = useEnvVariables();

  const _isLoading =
    isLoadingInterfaceRecord || isLoadingOverride || permissionsLoading || envVariablesLoading;
  const _error = errorInterfaceRecord || permissionsError || envVariablesError;

  debugSSR(
    'useAuthAppResourcesFromRecord',
    {
      isLoading: _isLoading,
      isLoadingInterfaceRecord,
      isLoadingPermissions: permissionsLoading,
      isLoadingEnvVariables: envVariablesLoading,
    },
    {
      interfaceId: interfaceRecord?.id,
      extraInfo: {
        interfaceRecord,
      },
    },
  );

  return {
    isLoading: _isLoading,
    error: _error,
    interfaceRecord,
    permissions,
    envVariables,
  };
}
